/**
 * The blue dot (spec §16): where a GPS fix falls on a placed sheet. Pure maths over the revision's geo block, so it
 * runs offline and is karma-testable; proj4 is passed in (the viewer lazy-loads it) rather than imported here.
 *
 * `page_geotransform` [a, b, c, d, e, f] maps the normalised page point (x, y; pdf.js viewport, top-left origin, R19)
 * to the CRS: E = a + b·x + c·y, N = d + e·x + f·y. `proj4` is the CRS as a +proj string or WKT (R36); proj4js reads
 * both.
 */

export interface SheetGeo {
  proj4: string;
  page_geotransform: number[];
  status: 'verified' | 'approximate';
  check_error_m: number | null;
  layer_uuid?: string;
}

/** The part of proj4js this needs: proj4(from, to) gives a converter between two definitions */
export type Proj4Fn = (from: string, to: string) => { forward(p: number[]): number[]; inverse(p: number[]): number[] };

export interface SheetPosition {
  /** Normalised page point; outside 0–1 when the fix is off the sheet */
  x: number;
  y: number;
  inside: boolean;
  /** Ground metres per 1.0 of normalised x at that point (for the accuracy circle) */
  metresPerUnit: number;
}

export interface OffSheet {
  /** Where the arrow sits: the fix's point clamped to the sheet edge */
  edgeX: number;
  edgeY: number;
  /** Screen direction from the sheet's centre to the fix, degrees clockwise from the right (east on screen) */
  angle: number;
}

const WGS84 = '+proj=longlat +datum=WGS84 +no_defs';
const EARTH_RADIUS_M = 6371008.8;
const COMPASS = ['north', 'northeast', 'east', 'southeast', 'south', 'southwest', 'west', 'northwest'];

/** One converter per CRS definition: building it parses the definition (WKT especially). */
const converters = new Map<string, ReturnType<Proj4Fn>>();

function converter(proj: Proj4Fn, definition: string): ReturnType<Proj4Fn> {
  let c = converters.get(definition);
  if (!c) {
    c = proj(WGS84, definition);
    converters.set(definition, c);
  }
  return c;
}

function usable(geo: SheetGeo | null | undefined): number[] | null {
  const gt = geo?.page_geotransform;
  if (!geo?.proj4 || !Array.isArray(gt) || gt.length !== 6 || !gt.every(Number.isFinite)) {
    return null;
  }
  return gt[1] * gt[5] - gt[2] * gt[4] !== 0 ? gt : null;
}

/** Ground point (lon, lat) of a normalised page point */
export function sheetToLonLat(x: number, y: number, geo: SheetGeo, proj: Proj4Fn): [number, number] | null {
  const gt = usable(geo);
  if (!gt) {
    return null;
  }
  try {
    const [lon, lat] = converter(proj, geo.proj4).inverse([gt[0] + gt[1] * x + gt[2] * y, gt[3] + gt[4] * x + gt[5] * y]);
    return Number.isFinite(lon) && Number.isFinite(lat) ? [lon, lat] : null;
  } catch {
    // A definition proj4 cannot read: no position rather than an error
    return null;
  }
}

/** Great-circle metres between two (lon, lat) points */
export function groundMetres(a: [number, number], b: [number, number]): number {
  const rad = Math.PI / 180;
  const dLat = (b[1] - a[1]) * rad;
  const dLon = (b[0] - a[0]) * rad;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(a[1] * rad) * Math.cos(b[1] * rad) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

/**
 * Where (lat, lon) falls on the sheet: project to the CRS, invert the affine. Null without a usable geo block or when
 * the point cannot be projected (proj4 returns non-finite numbers far outside a zone).
 */
export function projectToSheet(lat: number, lon: number, geo: SheetGeo | null | undefined, proj: Proj4Fn): SheetPosition | null {
  const gt = usable(geo);
  if (!gt || !Number.isFinite(lat) || !Number.isFinite(lon)) {
    return null;
  }
  let e: number;
  let n: number;
  try {
    [e, n] = converter(proj, geo.proj4).forward([lon, lat]);
  } catch {
    return null;
  }
  if (!Number.isFinite(e) || !Number.isFinite(n)) {
    return null;
  }
  const det = gt[1] * gt[5] - gt[2] * gt[4];
  const x = (gt[5] * (e - gt[0]) - gt[2] * (n - gt[3])) / det;
  const y = (gt[1] * (n - gt[3]) - gt[4] * (e - gt[0])) / det;
  // Metres per sheet unit measured on the ground, so any CRS unit (metres, US feet, degrees) works.
  const step = 0.01;
  const a = sheetToLonLat(x, y, geo, proj);
  const b = sheetToLonLat(x + step, y, geo, proj);
  const metresPerUnit = a && b ? groundMetres(a, b) / step : 0;
  return { x, y, inside: x >= 0 && x <= 1 && y >= 0 && y <= 1, metresPerUnit };
}

/**
 * The edge arrow for a fix off the sheet: the clamped point and the screen direction from the sheet's centre. The
 * angle is taken in pixels, so `aspect` (page width / height) matters: x and y are fractions of different lengths.
 */
export function offSheetBearing(x: number, y: number, aspect = 1): OffSheet {
  const clamp = (v: number) => Math.min(1, Math.max(0, v));
  const a = aspect > 0 && Number.isFinite(aspect) ? aspect : 1;
  const angle = (Math.atan2(y - 0.5, (x - 0.5) * a) * 180) / Math.PI;
  // To a tenth of a degree, in [0, 360): a fix level with the centre is 0, never 359.99…
  return { edgeX: clamp(x), edgeY: clamp(y), angle: (Math.round(((angle + 360) % 360) * 10) / 10) % 360 };
}

/**
 * "You are 240 m east of this sheet": the ground distance from the nearest sheet edge point to the fix, and the
 * compass direction of the fix from it (8 points). Null when the edge cannot be placed on the ground.
 */
export function offSheetText(lat: number, lon: number, position: SheetPosition, geo: SheetGeo, proj: Proj4Fn): string | null {
  const edge = offSheetBearing(position.x, position.y);
  const from = sheetToLonLat(edge.edgeX, edge.edgeY, geo, proj);
  if (!from) {
    return null;
  }
  const to: [number, number] = [lon, lat];
  const metres = groundMetres(from, to);
  const rad = Math.PI / 180;
  const bearing = (Math.atan2(
    Math.sin((to[0] - from[0]) * rad) * Math.cos(to[1] * rad),
    Math.cos(from[1] * rad) * Math.sin(to[1] * rad) - Math.sin(from[1] * rad) * Math.cos(to[1] * rad) * Math.cos((to[0] - from[0]) * rad),
  ) / rad + 360) % 360;
  return `You are ${distanceText(metres)} ${COMPASS[Math.round(bearing / 45) % 8]} of this sheet`;
}

/** 240 m (to the nearest 10 m below a kilometre), 1.3 km above */
export function distanceText(metres: number): string {
  if (metres < 1000) {
    return `${Math.max(10, Math.round(metres / 10) * 10)} m`;
  }
  return `${(metres / 1000).toFixed(metres < 10000 ? 1 : 0)} km`;
}

/** The accuracy circle's radius in sheet widths (accuracy metres over metres per sheet unit) */
export function accuracyRadius(accuracyM: number | null | undefined, position: SheetPosition): number {
  return accuracyM && accuracyM > 0 && position.metresPerUnit > 0 ? accuracyM / position.metresPerUnit : 0;
}
